/*
 * Copyright 2026 North Carolina State University
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WaterScheduleFields from '../shared/WaterScheduleFields';
import {
  emptyScheduleEntry,
  describeDays,
  formatDuration,
  formatScheduleRange,
  isScheduleCurrent,
  isScheduleUpcoming
} from '../../utils/waterSchedule';

/**
 * Watering schedules for a project, as the facility actually runs them.
 *
 * A schedule is effective-dated. When the watering genuinely changes, the
 * record in effect is ended and a new one added, so the project keeps an
 * answerable history — "what were they watering with in March" — instead of
 * only its latest state. Editing rewrites a record in place and is for fixing
 * what was typed.
 *
 * Clients ask for changes through a Compartment Environmental Adjustment
 * request; an admin records the result here.
 */

const todayISO = () => new Date().toISOString().slice(0, 10);

const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');

const personName = (p) => {
  if (!p || typeof p === 'string') return '';
  return `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || '';
};

/**
 * Add a schedule, or edit one that exists.
 *
 * To replace a schedule that is genuinely changing, end the current one and
 * add the new one — that is what keeps the project's history readable. Editing
 * rewrites the record in place and is for fixing what was typed.
 */
const ScheduleDialog = ({ open, mode, initial, waters, compartments, onClose, onSave }) => {
  const [entry, setEntry] = useState(emptyScheduleEntry());
  const [waterId, setWaterId] = useState('');
  const [chamberId, setChamberId] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = initial || {};
    setEntry({
      ...emptyScheduleEntry(),
      week1: base.week1 || emptyScheduleEntry().week1,
      week2: base.week2 || emptyScheduleEntry().week2,
      times_per_day: base.times_per_day ?? '',
      duration: base.duration || { hours: '', minutes: '', seconds: '' },
      comments: base.comments || ''
    });
    setWaterId(base.water?._id ? String(base.water._id) : (base.water ? String(base.water) : ''));
    setChamberId(base.chamber?._id ? String(base.chamber._id) : (base.chamber ? String(base.chamber) : ''));
    // An edit keeps the dates the record already has; a new schedule starts
    // today unless the admin dates it otherwise.
    setStartDate(mode === 'edit' ? toDateInput(base.start_date) || todayISO() : todayISO());
    setEndDate(mode === 'edit' ? toDateInput(base.end_date) : '');
  }, [open, initial, mode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const water = waters.find(w => String(w._id) === waterId);
      const compartment = compartments.find(c => String(c._id) === chamberId);
      await onSave({
        ...entry,
        water: waterId || null,
        water_label: water ? (water.label || water.description) : (entry.water_label || ''),
        chamber: chamberId || null,
        chamber_name: compartment?.name || '',
        facility: compartment?.facility_id || null,
        start_date: startDate,
        end_date: endDate || null
      });
      onClose();
    } catch {
      // onSave reports its own failure.
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'edit' ? 'Edit watering schedule' : 'Add a watering schedule';
  // A schedule belongs to a compartment, so one has to be named.
  const canSave = Boolean(chamberId);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {mode === 'edit' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This rewrites the record in place, so use it to fix something entered wrongly. If the
            watering has genuinely changed, end this schedule instead and add the new one — that is
            what keeps the project's history readable.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Any schedule in effect for the same compartment and water is ended the day before this
            one starts. Nothing is overwritten — the old schedule stays on record with its dates.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select fullWidth size="small" label="Water source"
            value={waterId}
            onChange={(e) => setWaterId(e.target.value)}
          >
            <MenuItem value="">— none —</MenuItem>
            {waters.map(w => (
              <MenuItem key={w._id} value={String(w._id)}>{w.label || w.description}</MenuItem>
            ))}
          </TextField>
          <TextField
            select fullWidth size="small" required label="Compartment"
            value={chamberId}
            onChange={(e) => setChamberId(e.target.value)}
            error={!chamberId}
            helperText={compartments.length === 0
              ? 'This project has no compartments assigned at the moment.'
              : 'The schedule applies to this compartment and the assignments the project holds in it.'}
          >
            {compartments.map(c => (
              <MenuItem key={c._id} value={String(c._id)}>{c.label}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            type="date" fullWidth size="small" label="In effect from"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            type="date" fullWidth size="small" label="Until (optional)"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            helperText="Leave blank while it is ongoing"
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <WaterScheduleFields entry={entry} onChange={setEntry} disabled={saving} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ScheduleCard = ({ schedule, onEdit, onEnd, onDelete }) => {
  const current = isScheduleCurrent(schedule);
  const scheduled = isScheduleUpcoming(schedule);
  const author = personName(schedule.created_by);
  const stateLabel = current ? 'In effect' : scheduled ? 'Scheduled' : 'Past';
  const stateColor = current ? 'success' : scheduled ? 'info' : 'default';
  // Only a schedule that is live or still to come can be stopped; a finished
  // one already has an end date.
  const isOpen = current || scheduled;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderLeft: '3px solid',
        borderLeftColor: current ? 'success.main' : scheduled ? 'info.main' : 'grey.400'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {schedule.water?.label || schedule.water_label || 'Water'}
        </Typography>
        <Chip size="small" label={stateLabel} color={stateColor} />
        {schedule.chamber && (
          <Chip size="small" variant="outlined" label={schedule.chamber.chamber || schedule.chamber_name} />
        )}
        {schedule.source === 'request' && (
          <Tooltip title="Recorded from a client request">
            <Chip size="small" variant="outlined" color="info" label="From request" />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit this schedule">
            <IconButton size="small" onClick={() => onEdit(schedule)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          {isOpen && (
            <Tooltip title="Stop this schedule on a date">
              <IconButton size="small" onClick={() => onEnd(schedule)}><EventBusyIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remove this record">
            <IconButton size="small" color="error" onClick={() => onDelete(schedule)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Typography variant="body2">{describeDays(schedule.week1, schedule.week2)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {schedule.times_per_day ? `${schedule.times_per_day}×/day` : 'Frequency not set'}
        {' · '}
        {formatDuration(schedule.duration)}
      </Typography>
      {schedule.comments && (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{schedule.comments}</Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {formatScheduleRange(schedule)}
        {author ? ` · recorded by ${author}` : ''}
      </Typography>
    </Paper>
  );
};

const WateringSchedulePane = ({ projectId, facilityId = null, refreshKey = 0 }) => {
  const [current, setCurrent] = useState([]);
  // A change can be dated forward, so a schedule that has not started yet is
  // its own bucket — calling it "in effect" would have an admin watering on a
  // pattern that is not live.
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [waters, setWaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [compartments, setCompartments] = useState([]);
  const [dialog, setDialog] = useState({ open: false, mode: 'add', initial: null });
  const [ending, setEnding] = useState(null);
  const [endDate, setEndDate] = useState(todayISO());
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(
        `/api/projects/${projectId}/environment-schedules?condition_type=water`
      );
      setCurrent(data.current || []);
      setUpcoming(data.upcoming || []);
      setPast(data.past || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load watering schedules:', err);
      setError('Could not load the watering schedules.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    const url = facilityId ? `/api/waters?facility=${facilityId}` : '/api/waters';
    axios.get(url)
      .then(({ data }) => { if (!cancelled) setWaters(Array.isArray(data) ? data : []); })
      .catch(err => console.error('Failed to load waters:', err));
    return () => { cancelled = true; };
  }, [facilityId]);

  /**
   * The compartments this project occupies right now — one entry per
   * compartment that holds at least one Active assignment, matching what the
   * client sees on the request form. Resolved from the assignments endpoint
   * rather than the project document so the names and facility are populated
   * and compartments the project has finished with are left out.
   */
  useEffect(() => {
    let cancelled = false;
    if (!projectId) return undefined;
    axios.get(`/api/projects/${projectId}/assignments`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = [];
        (data.assignments || []).forEach((ca) => {
          const active = (ca.assignments || []).filter(a => a.status === 'Active');
          if (active.length === 0 || !ca.chamber?._id) return;
          const name = ca.chamber.chamber || ca.chamber.description || 'Compartment';
          const unit = ca.chamber.capacity?.unit?.unit_short || '';
          const quantity = active.reduce((sum, a) => sum + (Number(a.quantity) || 0), 0);
          const parts = [name];
          if (quantity) parts.push(`${quantity} ${unit || 'units'}`);
          parts.push(`${active.length} assignment${active.length === 1 ? '' : 's'}`);
          list.push({
            _id: String(ca.chamber._id),
            name,
            label: parts.join(' · '),
            facility_id: ca.chamber.facility?._id || ca.chamber.facility || null,
            assignment_count: active.length
          });
        });
        setCompartments(list);
      })
      .catch(err => console.error('Failed to load compartments:', err));
    return () => { cancelled = true; };
  }, [projectId]);

  const save = async (payload) => {
    try {
      if (dialog.mode === 'edit' && dialog.initial?._id) {
        await axios.put(
          `/api/projects/${projectId}/environment-schedules/${dialog.initial._id}`,
          payload
        );
        toast.success('Schedule updated');
      } else {
        await axios.post(`/api/projects/${projectId}/environment-schedules`, {
          ...payload,
          condition_type: 'water',
          supersede: true
        });
        toast.success('Schedule added');
      }
      load();
    } catch (err) {
      console.error('Failed to save schedule:', err);
      toast.error(err.response?.data?.error || 'Failed to save the schedule');
      throw err;
    }
  };

  const confirmEnd = async () => {
    try {
      await axios.patch(
        `/api/projects/${projectId}/environment-schedules/${ending._id}/end`,
        { end_date: endDate }
      );
      toast.success('Schedule ended');
      setEnding(null);
      load();
    } catch (err) {
      console.error('Failed to end schedule:', err);
      toast.error(err.response?.data?.error || 'Failed to end the schedule');
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/projects/${projectId}/environment-schedules/${deleting._id}`);
      toast.success('Record removed');
      setDeleting(null);
      load();
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      toast.error(err.response?.data?.error || 'Failed to remove the record');
    }
  };

  const waterOptions = useMemo(() => waters, [waters]);

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          What the facility waters this project with, and what it used to be.
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialog({ open: true, mode: 'add', initial: null })}
        >
          Add
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        In effect ({current.length})
      </Typography>
      {current.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No watering schedule recorded for this project yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {current.map(s => (
            <ScheduleCard
              key={s._id}
              schedule={s}
              onEdit={(sch) => setDialog({ open: true, mode: 'edit', initial: sch })}
              onEnd={(sch) => { setEnding(sch); setEndDate(todayISO()); }}
              onDelete={setDeleting}
            />
          ))}
        </Stack>
      )}

      {upcoming.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Scheduled ({upcoming.length})
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {upcoming.map(s => (
              <ScheduleCard
                key={s._id}
                schedule={s}
                onEdit={(sch) => setDialog({ open: true, mode: 'edit', initial: sch })}
                onEnd={(sch) => { setEnding(sch); setEndDate(todayISO()); }}
                onDelete={setDeleting}
              />
            ))}
          </Stack>
        </>
      )}

      {past.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Past ({past.length})
          </Typography>
          <Stack spacing={1}>
            {past.map(s => (
              <ScheduleCard
                key={s._id}
                schedule={s}
                onEdit={(sch) => setDialog({ open: true, mode: 'edit', initial: sch })}
                onEnd={(sch) => { setEnding(sch); setEndDate(todayISO()); }}
                onDelete={setDeleting}
              />
            ))}
          </Stack>
        </>
      )}

      <ScheduleDialog
        open={dialog.open}
        mode={dialog.mode}
        initial={dialog.initial}
        waters={waterOptions}
        compartments={compartments}
        onClose={() => setDialog({ open: false, mode: 'add', initial: null })}
        onSave={save}
      />

      <Dialog open={Boolean(ending)} onClose={() => setEnding(null)}>
        <DialogTitle>End this schedule?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            It moves to the past list and keeps both of its dates.
          </DialogContentText>
          <TextField
            type="date" fullWidth size="small" label="Last day in effect"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnding(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmEnd}>End schedule</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Remove this record?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Use this only for a record that should never have existed. To stop a schedule that was
            genuinely in effect, end it instead so the project's history stays accurate.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WateringSchedulePane;
